import { useState, type ComponentProps } from "react";
import { useAuth } from "@clerk/react";
import { SaveIcon } from "lucide-react";
import { CreatorAction, PartnershipTier } from "@/types/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Frame, FrameFooter, FrameHeader, FramePanel, FrameTitle } from "@/components/ui/frame";
import { useDashboardStore, useEveActionsStore } from "@/stores";
import { statusAccentStyle } from "@/constants/statuses";
import { InfoRow } from "@/components/creators-table/phase/blocks";
import EmailThreadFrame from "./EmailThreadFrame";

const PAID_TYPES = new Set(["max_budget", "max_cpm", "custom_formula"]);

const plain = (n: number) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100));
const money = (n: number) => (Number.isInteger(n) ? `$${n.toLocaleString("en-US")}` : `$${n.toFixed(2)}`);

/** The campaign's own terms for a tier ("55% rev share + Flat fee (formula)"). */
function tierTermsLabel(tier: PartnershipTier): string {
  const parts = (tier.components ?? []).map((c) => {
    switch (c.type) {
      case "revenue_share":
        return c.value != null ? `${plain(c.value)}% rev share` : "Rev share";
      case "cpa":
        return c.value != null ? `${money(c.value)} CPA` : "CPA";
      case "max_budget":
        return c.value != null ? `Fee up to ${money(c.value)}` : "Flat fee";
      case "max_cpm":
        return c.value != null ? `Fee at ${money(c.value)} CPM` : "Flat fee";
      case "custom_formula":
        return "Flat fee (formula)";
      case "performance_cpm":
        return "Performance bonus";
      default:
        return "Other";
    }
  });
  return parts.join(" + ") || "No terms";
}

/** "" → null; anything else must parse as a positive number. */
function parse(value: string): number | null | "invalid" {
  const v = value.trim();
  if (!v) return null;
  const n = Number(v.replace(/[$,%\s]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : "invalid";
}

/**
 * Amy-v2 offer task (campaign eve_offer_review): Amy was about to reply on a
 * tier the team has not set for this creator, so she filed this task and
 * sent nothing. The team's fee / revenue share / CPA become the creator's
 * offer whatever the tier holds (amy tierWithOffer); saving answers it
 * through the amy set-creator-offer route, which hands the thread back to
 * her right away.
 *
 * Keyed by the creator: the Tasks page reuses one instance when you move
 * between two offer tasks, and a kept instance would carry the previous
 * creator's typed numbers (and never re-run the first field's autoFocus).
 */
export default function OfferReviewAction(props: {
  action: CreatorAction;
  emailThread: ComponentProps<typeof EmailThreadFrame>["emailThread"];
  threadLoading: boolean;
}) {
  return <OfferTask key={props.action._id} {...props} />;
}

function OfferTask({
  action,
  emailThread,
  threadLoading,
}: {
  action: CreatorAction;
  emailThread: ComponentProps<typeof EmailThreadFrame>["emailThread"];
  threadLoading: boolean;
}) {
  const { getToken } = useAuth();
  const setCreatorOffer = useEveActionsStore((s) => s.setCreatorOffer);
  const [saving, setSaving] = useState(false);

  const tierOrder = action.offer_request?.tier_order ?? action.current_tier_order ?? 1;
  const tier = (action.partnership?.tiers ?? []).find((t) => t.order === tierOrder);
  const parts = tier?.components ?? [];
  const valueOf = (type: string) => parts.find((c) => c.type === type)?.value ?? null;
  // The campaign's own numbers for this creator (amy stores them on the task).
  const terms = action.offer_request?.campaign_terms ?? { max_fee: null, revenue_share: valueOf("revenue_share"), cpa: valueOf("cpa") };
  const hasFee = parts.some((c) => PAID_TYPES.has(c.type));
  // A counter starts from the team's last offer; a first offer from the campaign's terms.
  const current = action.offer_request?.current_offer;
  const start = current ?? terms;
  const counter = action.offer_request?.reason === "counter" ? action.offer_request.creator_counter : undefined;
  // The campaign's fee budget not computable yet (a formula still missing CRM values): empty keeps it.
  const feePending = hasFee && (current ? !!current.keep_campaign_fee : terms.max_fee == null);
  // "Other" / performance parts stay in the offer as they are.
  const extras = parts.filter((c) => !PAID_TYPES.has(c.type) && c.type !== "revenue_share" && c.type !== "cpa");
  const str = (n: number | null | undefined) => (n != null ? String(n) : "");
  // Change a number, add a part, or clear one to leave it out.
  const [draft, setDraft] = useState({ maxFee: str(start.max_fee), revenueShare: str(start.revenue_share), cpa: str(start.cpa) });

  const parsed = { max_fee: parse(draft.maxFee), revenue_share: parse(draft.revenueShare), cpa: parse(draft.cpa) };
  const keepFee = feePending && parsed.max_fee === null;
  const empty = parsed.max_fee === null && !keepFee && parsed.revenue_share === null && parsed.cpa === null && extras.length === 0;
  const problem =
    Object.values(parsed).includes("invalid") || (typeof parsed.revenue_share === "number" && parsed.revenue_share > 100)
      ? "Use positive numbers, and a revenue share of at most 100%."
      : empty
        ? "Enter at least one number: a fee, a revenue share or a CPA."
        : null;

  const save = async () => {
    if (saving || problem) return;
    setSaving(true);
    try {
      const token = await getToken();
      if (!token) return;
      const ok = await setCreatorOffer(
        action._id,
        {
          max_fee: parsed.max_fee as number | null,
          revenue_share: parsed.revenue_share as number | null,
          cpa: parsed.cpa as number | null,
          keep_campaign_fee: keepFee,
        },
        token
      );
      if (ok) {
        useDashboardStore.setState((state) => ({
          tasks: state.tasks.filter((task) => !(task._id === action._id && task.actionType === "offer-review")),
        }));
      }
    } finally {
      setSaving(false);
    }
  };

  const input = (key: keyof typeof draft, label: string, hint: string, placeholder: string) => (
    <div className="flex flex-col gap-2 w-full min-w-0">
      <Label htmlFor={`offer-${key}`}>{label}</Label>
      <Input
        id={`offer-${key}`}
        inputMode="decimal"
        value={draft[key]}
        autoFocus={key === "maxFee"}
        placeholder={placeholder}
        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
        className="focus-visible:border-lime-800 focus-visible:ring-lime-800/10 dark:focus-visible:border-lime-400 dark:focus-visible:ring-lime-400/10 focus-visible:ring-3"
      />
      <span className="text-xs text-muted-foreground">{hint}</span>
    </div>
  );

  return (
    <div
      className="flex flex-col items-start gap-4 animate-in fade-in-0 slide-in-from-top-2 duration-250"
      style={statusAccentStyle("offer-review")}
    >
      <p className="text-sm text-muted-foreground">
        {counter
          ? `@${action.username} countered your offer and Amy is waiting for your answer. The fields start at your last offer: `
          : `Amy is about to make @${action.username} the tier ${tierOrder} offer and is waiting for you. The fields start at the campaign's terms: `}
        change a number, add a fee, revenue share or CPA, or clear one to leave it out.
      </p>

      <Frame className="w-full">
        <FrameHeader>
          <FrameTitle>{counter ? `Tier ${tierOrder} counter` : `Tier ${tierOrder} offer`}</FrameTitle>
        </FrameHeader>
        <FramePanel className="flex flex-col p-0 overflow-hidden">
          {tier && (
            <div className="flex flex-col divide-y border-b">
              <InfoRow label="Campaign terms">{tierTermsLabel(tier)}</InfoRow>
              {counter && <InfoRow label="They asked">{counter}</InfoRow>}
            </div>
          )}
          <div className="flex flex-col gap-4 p-4">
            <div className="flex md:flex-row flex-col gap-4">
              {input(
                "maxFee",
                "Max fee (USD)",
                terms.max_fee != null ? `Campaign: $${terms.max_fee}` : feePending ? "Empty = campaign budget" : "Not in campaign",
                feePending ? "Campaign budget" : "No fee"
              )}
              {input(
                "revenueShare",
                "Revenue share (%)",
                terms.revenue_share != null ? `Campaign: ${terms.revenue_share}%` : "Not in campaign",
                "No revenue share"
              )}
              {input("cpa", "CPA (USD)", terms.cpa != null ? `Campaign: $${terms.cpa}` : "Not in campaign", "No CPA")}
            </div>
            {problem && <p className="text-xs text-destructive">{problem}</p>}
          </div>
        </FramePanel>
        <FrameFooter className="flex items-center justify-end">
          <Button disabled={saving || !!problem} onClick={save} variant="neutral" size="sm">
            {saving ? <Spinner /> : <SaveIcon />}
            Save offer
          </Button>
        </FrameFooter>
      </Frame>

      <div className="w-full">
        <EmailThreadFrame action={action} emailThread={emailThread} threadLoading={threadLoading} />
      </div>
    </div>
  );
}
